/**
 * Every query key in the app, built here.
 *
 * Hierarchical on purpose: `['org', orgId, 'qr-codes', id]` sits under
 * `['org', orgId, 'qr-codes']` sits under `['org', orgId]`, so a mutation can
 * invalidate exactly the subtree it touched. React Query matches keys by prefix,
 * so `invalidateQueries({ queryKey: keys.org(orgId) })` catches everything
 * org-scoped without listing it.
 *
 * Scoping by organization rather than globally matters for correctness, not
 * tidiness: switching organizations must not show the previous one's codes for a
 * frame, and an org id in the key is what guarantees that.
 */
export const keys = {
  session: () => ['session'] as const,

  org: (organizationId: string) => ['org', organizationId] as const,

  /* Keyed by SLUG, not id — this is the query that resolves one into the other,
     so it cannot live under `org(id)`. */
  organizationBySlug: (slug: string) => ['organization', 'slug', slug] as const,

  qrCodes: (organizationId: string) =>
    [...keys.org(organizationId), 'qr-codes'] as const,

  qrCode: (organizationId: string, constellationId: string) =>
    [...keys.qrCodes(organizationId), constellationId] as const,

  groups: (organizationId: string) =>
    [...keys.org(organizationId), 'groups'] as const,

  members: (organizationId: string) =>
    [...keys.org(organizationId), 'members'] as const,

  billing: (organizationId: string) =>
    [...keys.org(organizationId), 'billing'] as const,

  notifications: () => ['notifications'] as const,

  /* Analytics is keyed under the code it describes so repointing or deleting a
     code drops its numbers with it. */
  analytics: (organizationId: string, constellationId: string) =>
    [...keys.qrCode(organizationId, constellationId), 'analytics'] as const,

  analyticsSummary: (
    organizationId: string,
    constellationId: string,
    timeZone: string,
  ) =>
    [...keys.analytics(organizationId, constellationId), 'summary', timeZone] as const,

  analyticsHourly: (
    organizationId: string,
    constellationId: string,
    timeZone: string,
  ) =>
    [...keys.analytics(organizationId, constellationId), 'hourly', timeZone] as const,

  analyticsDaily: (
    organizationId: string,
    constellationId: string,
    timeZone: string,
    days: number,
  ) =>
    [
      ...keys.analytics(organizationId, constellationId),
      'daily',
      timeZone,
      days,
    ] as const,

  /* `limit` is part of the key because it is part of the REQUEST — the route
     takes it and returns that many rows. Leaving it out let two callers asking
     for different numbers of places share one cache entry, so whichever ran
     first decided how many the other one got. */
  analyticsLocations: (
    organizationId: string,
    constellationId: string,
    days: number,
    limit: number,
  ) =>
    [
      ...keys.analytics(organizationId, constellationId),
      'locations',
      days,
      limit,
    ] as const,

  /** The batched per-code totals the grid reads. */
  orgScanTotals: (organizationId: string) =>
    [...keys.org(organizationId), 'scan-totals'] as const,

  search: (organizationId: string, term: string) =>
    [...keys.org(organizationId), 'search', term] as const,

  icons: (term: string) => ['icons', term] as const,

  /* Platform administration. Deliberately NOT under an org — the answer is the
     same whichever workspace an administrator happens to be standing in. */
  administratorSelf: (userId: string) => ['admin', 'self', userId] as const,
  /* Keyed by the search pattern: each term is its own paged list, so typing
     starts a fresh one instead of appending to the previous term's pages. */
  adminOrganizations: (pattern: string) =>
    ['admin', 'organizations', pattern] as const,
  /* Unparameterized: the screen groups and filters what it has loaded, so there
     is one list rather than one per view. */
  adminAppVersions: () => ['admin', 'app-versions'] as const,
}
